<script setup>
/**
 * SwipeTutorial
 * One-time overlay showing an animated swipe gesture illustration.
 * Appears once per user on touch devices, auto-dismisses after 5s.
 */
import { ref, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from '../../../app/store/auth.js'
import { isSeenByUser, markSeenByUser } from '../../../core/tutorials/tutorialStorage.js'

const TUTORIAL_ID = 'catalog-swipe-hint'
const SHOW_DELAY = 2000
const AUTO_DISMISS_DELAY = 5000

const authStore = useAuthStore()
const visible = ref(false)
let showTimer = null
let dismissTimer = null

function dismiss() {
  visible.value = false
  const uid = authStore.userId || 'anonymous'
  markSeenByUser(uid, TUTORIAL_ID)
  clearTimeout(dismissTimer)
}

onMounted(() => {
  // Only show on touch devices
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0
  if (!isTouchDevice) return

  const uid = authStore.userId || 'anonymous'
  if (isSeenByUser(uid, TUTORIAL_ID)) return

  showTimer = setTimeout(() => {
    visible.value = true
    dismissTimer = setTimeout(dismiss, AUTO_DISMISS_DELAY)
  }, SHOW_DELAY)
})

onUnmounted(() => {
  clearTimeout(showTimer)
  clearTimeout(dismissTimer)
})
</script>

<template>
  <Transition name="swipe-hint">
    <div v-if="visible" class="swipe-hint-overlay" @click="dismiss">
      <div class="swipe-hint-card" @click.stop="dismiss">
        <!-- Animated SVG: Hand with swipe arrows -->
        <div class="swipe-hint-illustration">
          <svg
            viewBox="0 0 200 80"
            width="200"
            height="80"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <!-- Left arrow -->
            <g class="swipe-arrow arrow-left">
              <path
                d="M30 40 L10 40"
                stroke="var(--color-info, #3b82f6)"
                stroke-width="2.5"
                stroke-linecap="round"
              />
              <path
                d="M18 33 L10 40 L18 47"
                stroke="var(--color-info, #3b82f6)"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                fill="none"
              />
            </g>

            <!-- Right arrow -->
            <g class="swipe-arrow arrow-right">
              <path
                d="M170 40 L190 40"
                stroke="var(--color-info, #3b82f6)"
                stroke-width="2.5"
                stroke-linecap="round"
              />
              <path
                d="M182 33 L190 40 L182 47"
                stroke="var(--color-info, #3b82f6)"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                fill="none"
              />
            </g>

            <!-- Dotted trail -->
            <line
              x1="50"
              y1="40"
              x2="150"
              y2="40"
              stroke="var(--color-info, #3b82f6)"
              stroke-width="1.5"
              stroke-dasharray="4 6"
              opacity="0.35"
            />

            <!-- Hand icon (centered, animated) -->
            <g class="swipe-hand">
              <!-- Palm -->
              <path
                d="M92 52 C89 52 87 50 87 47 L87 30 C87 28.3 88.3 27 90 27 C91.7 27 93 28.3 93 30 L93 25 C93 23.3 94.3 22 96 22 C97.7 22 99 23.3 99 25 L99 23 C99 21.3 100.3 20 102 20 C103.7 20 105 21.3 105 23 L105 25 C105 23.3 106.3 22 108 22 C109.7 22 111 23.3 111 25 L111 38 C111 38 114 35 116 36 C118 37 117 39 115 42 L111 50 C109 53 106 55 102 55 L97 55 C94 55 92 53 92 52 Z"
                stroke="var(--color-primary, #283548)"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                fill="var(--bg-primary, #ffffff)"
              />
              <!-- Index finger line -->
              <line
                x1="93"
                y1="30"
                x2="93"
                y2="38"
                stroke="var(--color-primary, #283548)"
                stroke-width="1"
                opacity="0.3"
              />
              <!-- Middle finger line -->
              <line
                x1="99"
                y1="25"
                x2="99"
                y2="38"
                stroke="var(--color-primary, #283548)"
                stroke-width="1"
                opacity="0.3"
              />
              <!-- Ring finger line -->
              <line
                x1="105"
                y1="25"
                x2="105"
                y2="38"
                stroke="var(--color-primary, #283548)"
                stroke-width="1"
                opacity="0.3"
              />
            </g>
          </svg>
        </div>

        <p class="swipe-hint-text">החליקו ימינה ושמאלה לניווט בין תתי מחלקות</p>

        <button class="swipe-hint-dismiss" @click="dismiss">הבנתי</button>
      </div>
    </div>
  </Transition>
</template>

<style scoped>
.swipe-hint-overlay {
  position: fixed;
  inset: 0;
  /* Above the mobile bottom bar (MobileCartFooter z-index: 999) */
  z-index: 1600;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  background: rgba(0, 0, 0, 0.15);
}

.swipe-hint-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 20px 24px 16px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  max-width: 320px;
  width: 100%;
  z-index: 1000;
}

.swipe-hint-illustration {
  display: flex;
  align-items: center;
  justify-content: center;
}

.swipe-hint-text {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary, #0f172a);
  text-align: center;
  line-height: 1.5;
  margin: 0;
}

.swipe-hint-dismiss {
  padding: 8px 28px;
  background: var(--color-primary, #283548);
  color: #fff;
  border: none;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}

.swipe-hint-dismiss:hover {
  background: var(--color-primary-hover, #1e293b);
}

/* ── Hand swipe animation ──────────────────────── */
.swipe-hand {
  animation: handSwipe 2s ease-in-out infinite;
}

@keyframes handSwipe {
  0%,
  100% {
    transform: translateX(0);
  }
  30% {
    transform: translateX(20px);
  }
  70% {
    transform: translateX(-20px);
  }
}

/* ── Arrow pulse animation ─────────────────────── */
.swipe-arrow {
  animation: arrowPulse 2s ease-in-out infinite;
}

.arrow-left {
  animation-delay: 0.5s;
}

.arrow-right {
  animation-delay: 0s;
}

@keyframes arrowPulse {
  0%,
  100% {
    opacity: 0.4;
  }
  50% {
    opacity: 1;
  }
}

/* ── Enter/Leave transitions ───────────────────── */
.swipe-hint-enter-active {
  transition: opacity 0.3s ease;
}

.swipe-hint-enter-active .swipe-hint-card {
  transition:
    transform 0.3s ease,
    opacity 0.3s ease;
}

.swipe-hint-leave-active {
  transition: opacity 0.25s ease;
}

.swipe-hint-leave-active .swipe-hint-card {
  transition:
    transform 0.25s ease,
    opacity 0.25s ease;
}

.swipe-hint-enter-from {
  opacity: 0;
}

.swipe-hint-enter-from .swipe-hint-card {
  opacity: 0;
  transform: translateY(30px);
}

.swipe-hint-leave-to {
  opacity: 0;
}

.swipe-hint-leave-to .swipe-hint-card {
  opacity: 0;
  transform: translateY(20px);
}
</style>
